/* eslint-disable no-underscore-dangle */
import React from "react";
import fpMap from "lodash/fp/map";
import curry from "lodash/fp/curry";
import { FormattedMessage } from "react-intl";

import { ContentItemMapper, AreasMapper } from "../mappers";
import { getLang } from "../../../js/utils/functions";
import { MAX_NUM_IMAGES } from "../config";

const lang = getLang;

/**
 * Gets an array of base 64 images and converts it to a format valid to be sent
 * @param {Array} images -
 */
export const processImages = (images, size) => {
  return images.map((image, index) => {
    const { base64: s3Image, name: s3ImageFileName } = image;
    return ContentItemMapper.dehydrate({
      _destroy: false, // TODO -- it will be true when the user wants to delete it
      position: size + 1 + index,
      language: lang,
      type: "image",
      s3Image,
      s3ImageFileName,
      caption: null,
      embedUrl: null,
      description: null,
    });
  });
};

/**
 * It creates a new content item for the form.
 * @param {number} index - position of the element within the content items array.
 * @param {String} type - type of the element (image, embed, text).
 */
export const createContentItem = (index, type) => {
  const id = new Date().getTime().toString();

  return ContentItemMapper.hydrate({
    _destroy: false,
    position: parseInt(index, 10) + 1,
    language: lang,
    contentId: id,
    humanizedItemType: type,
  });
};

/**
 * It removes a content item from the store.
 * @param {Array} contentItems - set of original content items.
 * @param {String} id - id of the item to erase.
 * @return {Array} - new content items array.
 */
export const removeContentItem = (contentItems, id) => {
  if (!id) return contentItems;

  const newContentItems = contentItems;

  newContentItems.forEach(contentItem => {
    if (contentItem.id === id || contentItem.contentId === id) {
      // eslint-disable-next-line no-underscore-dangle
      // eslint-disable-next-line no-param-reassign
      contentItem._destroy = true;
      contentItem.position = null;
    }
  });

  return newContentItems;
};

/**
 * Updates key for a content item.
 * @param {String} key - key to update.
 * @param {String} value - value to assign to the key.
 * @param {String} contentId - id of the content item to update.
 * @param {Object} item - content item object.
 */
export const updateContentItem = (key, value, id, item) => {
  if (
    parseInt(item.id, 10) === parseInt(id, 10) ||
    parseInt(item.contentId, 10) === parseInt(id, 10)
  ) {
    item[key] = value;
  }
  return item;
};

const curryUpdateContentItem = curry(updateContentItem);

/**
 * Loops through the content items array to update a specific item.
 * @param {Object} action - object with
 * @param {Object} state - redux state.
 */
export const updateContentItems = (action, state) => {
  const { key, value, elementId } = action;
  if (!elementId || !key) {
    return state.contentItems; // return current contentItems
  }
  return fpMap(curryUpdateContentItem(key, value, elementId))(state.contentItems);
};

/**
 * Serialize the content items for send querys
 * @param {Object} contentItems - Array with content items
 */
export const serializeContentItems = contentItems => {
  let items = "";

  contentItems.forEach(item => {
    if (item.type === "image") {
      const { _destroy, position, type, s3Image, caption, s3ImageFileName, id } = item;
      items += `{ _destroy: ${_destroy}, position: ${position}, language: "${lang}", type: "${type}"`;

      if (id) {
        items += `, id: "${id}"`;
      }

      if (s3Image) {
        items += `, s3Image: "${s3Image}"`;
      }

      if (s3ImageFileName) {
        items += `, s3ImageFileName: "${s3ImageFileName}"`;
      }

      items += `, caption: "${caption}" },`;
    }

    if (item.type === "embed") {
      const { _destroy, position, type, embedUrl, id } = item;

      items += `{ _destroy: ${_destroy}`;

      if (id) {
        items += `, id: "${id}"`;
      }

      items += `, position: ${position}, language: "${lang}", type: "${type}", embedUrl: "${embedUrl}" },`;
    }

    if (item.type === "text") {
      const { _destroy, position, type, description, id } = item;

      items += `{ _destroy: ${_destroy}`;

      if (id) {
        items += `, id: "${id}"`;
      }

      if (description) {
        items += `, description: "${description}"`;
      }

      items += `, position: ${position}, language: "${lang}", type: "${type}" },`;
    }
  });

  return items;
};

/**
 * Filter the null images from the contentItems array if there's more than one
 * @param {Object} contentItems - Array with contentItems
 */
export const filterContentItems = contentItems => {
  if (!contentItems || typeof contentItems !== "object" || contentItems.length === 0) {
    return [];
  }

  if (contentItems.length === 1) {
    return contentItems.map(contentItem => ContentItemMapper.hydrate(contentItem));
  }

  // let's filter every correct image received from BE
  const filteredContentItems = contentItems.filter(
    item => !(item.humanizedItemType === "image" && !item.id)
  );

  // If there are more than one, we consider the user tried to upload several images via the multi-image uploader
  // we don't process any image then
  if (contentItems.length - filteredContentItems.length > 1) {
    return filteredContentItems.map(contentItem => ContentItemMapper.hydrate(contentItem));
  }

  // We process the whole contentItems array otherwise
  return contentItems.map(contentItem => ContentItemMapper.hydrate(contentItem));
};

/**
 * Creates a set of reduced info about our content items so we can pass it to the OrganizeModal.
 * It also rewrites its initial position.
 * @param {Object} contentItems - Array with contentItems
 */
export const contentItemsSummary = contentItems => {
  if (typeof contentItems !== "object" || contentItems.length === 0) {
    return [];
  }
  const itemsSummary = [];
  const defaultDescriptions = {
    text: I18n.t("projects.new.more_content_section.organize.text"),
    image: I18n.t("projects.new.more_content_section.organize.image"),
    embed: I18n.t("projects.new.more_content_section.organize.video"),
  };
  const validItems = contentItems.filter(item => item._destroy === false);
  validItems.map((contentItem, index) => {
    const { type, id, contentId, _destroy } = contentItem;

    // check if the type is correct
    if (!["image", "s3image", "embed", "text"].includes(type)) {
      return null;
    }

    const summarizedItem = {};

    summarizedItem.position = index;
    summarizedItem.id = id || contentId;
    summarizedItem._destroy = _destroy;

    if (type === "image" || type === "s3image") {
      const { caption, s3ImageFileName, url } = contentItem;
      summarizedItem.type = "image";

      let description = defaultDescriptions.image;

      if (caption) {
        description = caption;
      } else if (s3ImageFileName) {
        description = s3ImageFileName;
      }

      summarizedItem.description = description;

      if (url) {
        summarizedItem.url = url;
      }
    }

    if (type === "embed") {
      const { embedTitle, embedUrl } = contentItem;
      summarizedItem.type = "embed";
      let description = defaultDescriptions.embed;
      if (embedTitle) {
        description = embedTitle;
      }

      summarizedItem.description = description;

      if (embedUrl) {
        summarizedItem.url = embedUrl;
      }
    }

    if (type === "text") {
      const { description: originalDescription } = contentItem;
      summarizedItem.type = "text";
      let description = defaultDescriptions.text;
      if (
        originalDescription &&
        originalDescription.length > 0 &&
        originalDescription !== "" &&
        originalDescription !== "\n"
      ) {
        description = originalDescription;
      }

      summarizedItem.description = description;
    }

    itemsSummary.push(summarizedItem);
  });

  return itemsSummary;
};

/**
 * Update content items array to the positions specified by the updated array.
 * @param {Object} contentItems - Array with the original contentItems
 * @param {Object} updatedContentItems - Array with the updated positions
 */
export const updatePositions = (contentItems, updatedContentItems) => {
  const contentItemsUpdated = [];

  updatedContentItems.forEach(sortedItem => {
    const contentItem = contentItems.find(item => (item.id || item.contentId) === sortedItem.id);

    contentItem.position = parseInt(sortedItem.newPosition, 10) + 1; // positions begin in 1
    contentItemsUpdated.push(contentItem);
  });

  return contentItemsUpdated;
};

/**
 * Filters objects array getting those without a certain key.
 * @param {Array} values - array of objects.
 * @param {String} key
 * @return {Array} - new objects array.
 */
export const filterValues = (values, key) => {
  if (!values) {
    return [];
  }

  if (!key) {
    return values;
  }

  return values.filter(value => !Object.prototype.hasOwnProperty.call(value, key));
};

/**
 * Find if exist any error in data
 * @param {Object} data - Object with data information
 */
export const findErrorsInProject = data => {
  const projectErrors = (data.errors || []).reduce((obj, e) => {
    return { ...obj, [e.key]: e.messages };
  }, {});

  const contentItemsErrors = (data.contentItems || []).reduce((obj, item) => {
    const itemErrors = (item.errors || []).map(e => e.messages).flat();

    if (itemErrors.length > 0) {
      return { ...obj, [item.position]: itemErrors };
    } else {
      return { ...obj };
    }
  }, {});

  if (Object.keys(contentItemsErrors).length > 0) {
    return { ...projectErrors, contentItems: contentItemsErrors };
  } else {
    return { ...projectErrors };
  }
};

/**
 * Set array with posible errors in Uploader Component multiple
 */
export const errorsMessagesUploaderImageMultiple = [
  {
    type: "size",
    message: fileName => (
      <FormattedMessage
        id="projects.new.image_upload.too_large_multiple.error"
        values={{
          size: 10,
          fileName,
        }}
      />
    ),
  },
  {
    type: "wrongType",
    message: fileName => (
      <FormattedMessage
        id="projects.new.image_upload.invalid_file_type_multiple.error"
        values={{
          fileName,
        }}
      />
    ),
  },
  {
    type: "numFiles",
    message: () => (
      <FormattedMessage
        id="projects.new.image_upload.too_many_files.error"
        values={{
          numFiles: MAX_NUM_IMAGES,
        }}
      />
    ),
  },
];

/**
 * Set array with posible errors in Uploader Component single
 */
export const errorsMessageUploaderImageSingle = [
  {
    type: "dimensions.width_and_height",
    message: (_fileNames, minWidth, minHeight) => (
      <FormattedMessage
        id="projects.new.cover_image.input.dimensions.error"
        values={{
          minWidth,
          minHeight,
        }}
      />
    ),
  },
  {
    type: "dimensions.width",
    message: (_fileNames, minWidth) => (
      <FormattedMessage
        id="projects.new.cover_image.input.dimensions_width.error"
        values={{
          minWidth,
        }}
      />
    ),
  },
  {
    type: "dimensions.height",
    message: (_fileNames, minHeight) => (
      <FormattedMessage
        id="projects.new.cover_image.input.dimensions_height.error"
        values={{
          minHeight,
        }}
      />
    ),
  },
  {
    type: "size",
    message: fileName => (
      <FormattedMessage
        id="projects.new.image_upload.too_large.error"
        values={{
          size: 10,
          fileName,
        }}
      />
    ),
  },
  {
    type: "wrongType",
    message: fileName => (
      <FormattedMessage
        id="projects.new.image_upload.invalid_file_type.error"
        values={{
          fileName,
        }}
      />
    ),
  },
  {
    type: "numFiles",
    message: () => <FormattedMessage id="projects.new.cover_image.input.too_many_files.error" />,
  },
];

/**
 * Validates the form.
 * @param {Object} project - project info.
 * @return {Boolean}
 */
export const validateProject = project => {
  if (!project) {
    return false;
  }

  if (!project.name.length) {
    return false;
  }

  const nonErasedItems = project.contentItems?.filter(item => item._destroy === false);

  if (nonErasedItems?.length <= 0) {
    return false;
  }

  const itemsWithError = nonErasedItems.find(contentItem => contentItem.errors?.length > 0);

  if (itemsWithError) {
    return false;
  }

  if (!project.areaIds || project.areaIds.length <= 0) {
    return false;
  }

  if (!project.cover || project.cover.length <= 0) {
    return false;
  }

  if (project.errorMessage && project.errorMessage.length > 0) {
    return false;
  }

  return true;
};

/**
 * Display error message with desired format
 */
export const handleErrorMessage = error => {
  return { exception: error?.message || error };
};

/**
 * Check that given response is valid
 */
export const validateResponseAndThrowIfNeeded = response => {
  if (
    !response ||
    !response.status ||
    !response.data ||
    response.status < 200 ||
    response.status > 300
  ) {
    throw response;
  }
};

/**
 * Sanitize given content, escaping or removing double quotes
 */
export const sanitizeDoubleQuotes = (content, { action }) => {
  const options = {
    escape: '\\"',
    remove: "",
  };

  return content.replace(/"/g, options[action]);
};

/**
 * Take areas already associated to the project or the ones associated to the course, if it exists
 */
export const loadDefaultAreas = project => {
  if (project.areaIdsSaved?.length) return project.areaIdsSaved;

  return project.course?.areas?.map(element => AreasMapper.hydrateSingle(element));
};
